import { useNavigate } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import {
  useCampaignCountQuery,
  useWalletBalanceQuery,
} from '@/features/dashboard/api/useDashboardQueries';
import {
  formatCompactMoney,
  formatCount,
} from '@/shared/lib/format/money';
import { useIsClient } from '@/shared/hooks/useIsClient';
import { useIsTeamMember } from '@/shared/hooks/useIsTeamMember';

export function DashboardCampaignsOverviewCard() {
  const navigate = useNavigate();
  const isClient = useIsClient();
  const isTeamMember = useIsTeamMember();
  const hideWalletMetrics = isClient || isTeamMember;
  const { data: walletRes, isLoading: walletLoading } = useWalletBalanceQuery({
    enabled: !hideWalletMetrics,
  });
  const { data: campaignRes, isLoading: campaignLoading } =
    useCampaignCountQuery();

  const available = walletRes?.data?.availableBalance ?? 0;
  const spent = walletRes?.data?.totalSpent ?? 0;
  const active = campaignRes?.data?.count ?? 0;
  const impressions = campaignRes?.data?.totalImpression ?? 0;

  // const availableNum = Number(available) || 0;
  // const spentNum = Number(spent) || 0;
  // const pool = availableNum + spentNum;
  // const percentage = pool > 0 ? Math.round((spentNum / pool) * 100) : 0;

  const showSyncAlert =
    !campaignLoading && Number(active) > 0 && Number(impressions) === 0;

  const metrics = [
    {
      label: 'Total active',
      value: campaignLoading ? '—' : formatCount(active),
      valueColor: '#2B3E8A',
      bg: '#E3ECFB',
    },
    {
      label: 'Total impressions',
      value: campaignLoading ? '—' : formatCount(impressions),
      valueColor: '#12785A',
      bg: '#DDF5EA',
    },
    ...(!hideWalletMetrics
      ? [
          {
            label: 'Total spend',
            value: walletLoading ? '—' : formatCompactMoney(spent),
            valueColor: '#A15C00',
            bg: '#FFF3DC',
          },
          {
            label: 'Available Balance',
            value: walletLoading ? '—' : formatCompactMoney(available),
            valueColor: '#5B3A9E',
            bg: '#EDE4F8',
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-full flex-col rounded-[28px] bg-white p-6 shadow-[0_10px_30px_rgba(60,40,120,0.05)] sm:p-7">
      <div className="mb-4 flex items-center justify-between gap-3">
        
        <h3 className="m-0 text-[18px] font-extrabold text-[#3A3660]">
          Active Campaigns
        </h3>
        <button
          type="button"
          onClick={() => navigate('/campaigns')}
          className="rounded-full border-none bg-[#DFE9FA] px-3 py-1 text-[12px] font-bold text-[#2B3E8A] transition hover:bg-[#D0E0F8]"
        >
          Overview
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="rounded-[16px] p-4 text-left"
            style={{ background: metric.bg }}
          >
            <p className="m-0 text-[12px] font-semibold text-[#4A4670]">
              {metric.label}
            </p>
            <p
              className="mt-1.5 mb-0 text-[24px] font-extrabold leading-tight tracking-[-0.02em]"
              style={{ color: metric.valueColor }}
            >
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      {showSyncAlert ? (
        <div className="mt-3.5 flex items-start gap-2.5 rounded-[14px] border border-[#F5DFA6] bg-[#FFF8E6] px-3.5 py-3 text-[13px] text-[#7A4B00]">
          <TriangleAlert size={16} className="mt-0.5 shrink-0 text-[#A15C00]" />
          <p className="m-0 leading-snug">
            Impressions read 0 — check screen sync status if campaigns have
            been live over 24h.
          </p>
        </div>
      ) : null}
    </div>
  );
}
